/**
 * ROILaunchDialog
 *
 * Reusable dialog that lets staff create + send a Release of Information
 * (ROI) e-signature request. Can be launched in two modes:
 *   - With `clientId` and `clientName` pre-set (from the Client Profile page)
 *   - Without (from the Faxes page) — staff picks a client first
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Send, Copy, ShieldCheck, CheckCircle, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INFO_CATEGORIES = [
  { key: "medical_records", label: "Medical Records" },
  { key: "plan_of_care", label: "Plan of Care" },
  { key: "billing_records", label: "Billing Records" },
  { key: "mental_health", label: "Mental Health Records" },
  { key: "substance_abuse", label: "Substance Abuse Records" },
  { key: "hiv_aids", label: "HIV / AIDS Information" },
  { key: "lab_results", label: "Lab Results" },
  { key: "other", label: "Other" },
];

export default function ROILaunchDialog({
  open,
  onOpenChange,
  clientId: presetClientId,
  clientName: presetClientName,
  clientEmail: presetClientEmail,
  clientPhone: presetClientPhone,
  onCreated,
}) {
  const [loadingClients, setLoadingClients] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(presetClientId || "");
  const [recipientName, setRecipientName] = useState("");
  const [recipientAddress, setRecipientAddress] = useState("");
  const [purpose, setPurpose] = useState("Continuity of Care");
  const [categories, setCategories] = useState(["medical_records", "plan_of_care"]);
  const [otherInfo, setOtherInfo] = useState("");
  const [expiration, setExpiration] = useState("Until Revoked");
  const [notes, setNotes] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [recipientEmail, setRecipientEmail] = useState(presetClientEmail || "");
  const [recipientPhone, setRecipientPhone] = useState(presetClientPhone || "");
  const [submitting, setSubmitting] = useState(false);
  const [createdLink, setCreatedLink] = useState(null);
  const [createdRoi, setCreatedRoi] = useState(null);
  const [createdEmailStatus, setCreatedEmailStatus] = useState(null);
  const [smsPhone, setSmsPhone] = useState("");
  const [sendingSms, setSendingSms] = useState(false);

  const needsClientPicker = !presetClientId;

  useEffect(() => {
    if (open && needsClientPicker) {
      fetchClients();
    }
    if (open) {
      setSelectedClientId(presetClientId || "");
      setRecipientEmail(presetClientEmail || "");
      setRecipientPhone(presetClientPhone || "");
      setCreatedLink(null);
      setCreatedRoi(null);
      setCreatedEmailStatus(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchClients = async () => {
    setLoadingClients(true);
    try {
      const res = await axios.get(`${API}/clients`);
      setClients(res.data || []);
    } catch (e) {
      toast.error("Failed to load clients");
    } finally {
      setLoadingClients(false);
    }
  };

  // Auto-fill recipient email when the staff picks a client from the dropdown
  // (only when the dialog wasn't pre-targeted to a specific client)
  useEffect(() => {
    if (!needsClientPicker || !selectedClientId) return;
    const c = clients.find((x) => x.id === selectedClientId);
    if (c && c.email) {
      setRecipientEmail(c.email);
    }
    if (c && c.phone) {
      setRecipientPhone(c.phone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId, clients]);

  const selectedClientLabel = useMemo(() => {
    if (presetClientName) return presetClientName;
    const c = clients.find((x) => x.id === selectedClientId);
    return c ? `${c.first_name} ${c.last_name}` : "";
  }, [clients, selectedClientId, presetClientName]);

  const toggleCategory = (key, checked) => {
    setCategories((prev) =>
      checked ? [...new Set([...prev, key])] : prev.filter((k) => k !== key)
    );
  };

  const handleSubmit = async () => {
    if (!selectedClientId) {
      toast.error("Please select a client");
      return;
    }
    if (sendEmail && !recipientEmail.trim()) {
      toast.error("Please enter the client's email or disable email sending");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        client_id: selectedClientId,
        recipient_name: recipientName || null,
        recipient_address: recipientAddress || null,
        purpose: purpose || "Continuity of Care",
        information_categories: categories,
        other_information: otherInfo || null,
        expiration: expiration || "Until Revoked",
        additional_notes: notes || null,
        send_to_email: sendEmail ? recipientEmail.trim() : null,
        send_to_phone: recipientPhone.trim() || null,
      };
      const res = await axios.post(`${API}/roi`, payload);
      setCreatedLink(res.data.secure_url);
      setCreatedRoi(res.data);
      setSmsPhone(res.data.send_to_phone || recipientPhone.trim());
      setCreatedEmailStatus(res.data.email_status);
      if (res.data.sms_status === "sent") {
        toast.success(res.data.sms_reason === "email_failed"
          ? "Email failed, so the signing link was texted to the client instead"
          : "ROI created and the signing link was texted to the client");
      } else if (sendEmail) {
        if (res.data.email_status === "sent") {
          if (res.data.email_used_fallback_sender) {
            toast.success(
              "ROI created and emailed — used our default sender because your custom domain isn't verified with Resend yet"
            );
          } else {
            toast.success("ROI created and email sent to client");
          }
        } else if (res.data.email_status === "error") {
          const detail = res.data.email_error || "Unknown error";
          toast.error(`ROI created but email failed: ${detail}. Use the link below to share manually.`);
        } else if (res.data.email_status === "skipped") {
          toast.warning(
            "ROI created — email service isn't configured. Contact support to enable Resend."
          );
        } else {
          toast.success("ROI created. Share the secure link below.");
        }
      } else {
        toast.success("ROI created. Share the secure link below.");
      }
      onCreated && onCreated(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create ROI");
    } finally {
      setSubmitting(false);
    }
  };

  const sendByText = async () => {
    if (!createdRoi || !smsPhone.trim()) {
      toast.error("Enter the client's mobile number");
      return;
    }
    setSendingSms(true);
    try {
      const res = await axios.post(`${API}/roi/${createdRoi.id}/send-sms`, { phone: smsPhone.trim() });
      setCreatedRoi((r) => ({ ...r, sms_status: "sent", send_to_phone: res.data.to }));
      toast.success(`Signing link texted to ${res.data.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Text message failed");
    } finally {
      setSendingSms(false);
    }
  };

  const copyLink = async () => {
    if (!createdLink) return;
    try {
      await navigator.clipboard.writeText(createdLink);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy link");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="roi-launch-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" />
            Send Release of Information
          </DialogTitle>
          <DialogDescription>
            Generate a secure e-signature link and email or text it to the client.
          </DialogDescription>
        </DialogHeader>

        {createdLink ? (
          <div className="space-y-4 py-2">
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5" />
              <div className="text-sm text-emerald-900">
                <p className="font-semibold">ROI request created</p>
                <p className="text-emerald-800 mt-1">
                  {createdEmailStatus === "sent"
                    ? "An email with the signature link was delivered to the client."
                    : createdRoi?.sms_status === "sent"
                    ? "The signature link was texted to the client's mobile."
                    : createdEmailStatus === "error"
                    ? "We couldn't send the email automatically — text the link below or copy and share it manually."
                    : "Email was not sent. Text the link or copy it below to share with the client."}
                </p>
                {createdRoi?.sms_status && createdRoi.sms_status !== "sent" && (
                  <p className="text-amber-800 mt-1 text-xs" data-testid="roi-sms-error">
                    Text message not sent: {createdRoi.sms_error}
                  </p>
                )}
              </div>
            </div>
            <div className="p-3 border rounded-lg space-y-2" data-testid="roi-sms-panel">
              <Label className="text-xs flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> Send by text message</Label>
              <div className="flex gap-2">
                <Input
                  type="tel"
                  value={smsPhone}
                  onChange={(e) => setSmsPhone(e.target.value)}
                  placeholder="Client mobile, e.g. 414-555-0142"
                  data-testid="roi-sms-phone-input"
                />
                <Button onClick={sendByText} variant={createdEmailStatus === "sent" ? "outline" : "default"} disabled={sendingSms} className="gap-2 shrink-0" data-testid="roi-send-sms-btn">
                  <Send className="w-4 h-4" />
                  {sendingSms ? "Sending..." : createdRoi?.sms_status === "sent" ? "Text again" : "Send by text"}
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs">Secure Signature Link</Label>
              <div className="mt-1 flex gap-2">
                <Input
                  value={createdLink}
                  readOnly
                  className="font-mono text-xs"
                  data-testid="roi-created-link-input"
                />
                <Button onClick={copyLink} variant="outline" data-testid="roi-copy-link-btn">
                  <Copy className="w-4 h-4 mr-2" />
                  Copy
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                This link does not expire and can be reused until the client signs.
              </p>
            </div>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)} data-testid="roi-done-btn">
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {/* Client selection */}
            {needsClientPicker ? (
              <div className="space-y-1">
                <Label>Client</Label>
                <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                  <SelectTrigger data-testid="roi-client-select">
                    <SelectValue placeholder={loadingClients ? "Loading clients..." : "Select a client"} />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.first_name} {c.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="p-3 bg-slate-50 border rounded-lg">
                <Label className="text-xs text-slate-500">Client</Label>
                <p className="font-medium text-slate-800">{selectedClientLabel}</p>
              </div>
            )}

            {/* Recipient */}
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Recipient of Disclosure</Label>
                <Input
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  placeholder="e.g., Dr. Smith / ABC Hospital"
                  data-testid="roi-recipient-name-input"
                />
              </div>
              <div className="space-y-1">
                <Label>Recipient Address (optional)</Label>
                <Input
                  value={recipientAddress}
                  onChange={(e) => setRecipientAddress(e.target.value)}
                  placeholder="Street, City, State ZIP"
                />
              </div>
              <div className="space-y-1">
                <Label>Purpose of Disclosure</Label>
                <Input
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  data-testid="roi-purpose-input"
                />
              </div>
              <div className="space-y-1">
                <Label>Expiration</Label>
                <Input
                  value={expiration}
                  onChange={(e) => setExpiration(e.target.value)}
                  placeholder="Until Revoked, or MM/DD/YYYY"
                />
              </div>
            </div>

            {/* Information categories */}
            <div className="space-y-2">
              <Label>Information Authorized for Disclosure</Label>
              <div className="grid grid-cols-2 gap-2 p-3 border rounded-lg bg-slate-50">
                {INFO_CATEGORIES.map((c) => (
                  <label
                    key={c.key}
                    className="flex items-center gap-2 text-sm cursor-pointer"
                  >
                    <Checkbox
                      checked={categories.includes(c.key)}
                      onCheckedChange={(v) => toggleCategory(c.key, !!v)}
                      data-testid={`roi-cat-${c.key}`}
                    />
                    {c.label}
                  </label>
                ))}
              </div>
              {categories.includes("other") && (
                <Input
                  value={otherInfo}
                  onChange={(e) => setOtherInfo(e.target.value)}
                  placeholder="Describe other information..."
                />
              )}
            </div>

            {/* Notes */}
            <div className="space-y-1">
              <Label>Additional Notes (optional)</Label>
              <Textarea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any extra context for this authorization..."
              />
            </div>

            {/* Email sending */}
            <div className="space-y-2 p-3 border rounded-lg">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={sendEmail}
                  onCheckedChange={(v) => setSendEmail(!!v)}
                  data-testid="roi-send-email-toggle"
                />
                Email the secure signature link to the client now
              </label>
              {sendEmail && (
                <div className="space-y-1">
                  <Label className="text-xs">Client Email Address</Label>
                  <Input
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder="client@example.com"
                    data-testid="roi-email-input"
                  />
                </div>
              )}
              {!sendEmail && (
                <p className="text-xs text-slate-500">
                  You&apos;ll get a secure link to copy/share manually after creation.
                </p>
              )}
              <div className="space-y-1 pt-1">
                <Label className="text-xs">Client Mobile (text fallback)</Label>
                <Input
                  type="tel"
                  value={recipientPhone}
                  onChange={(e) => setRecipientPhone(e.target.value)}
                  placeholder="414-555-0142"
                  data-testid="roi-phone-input"
                />
                <p className="text-[11px] text-slate-500">
                  {sendEmail
                    ? "If the email can't be delivered, the signing link is texted to this number automatically."
                    : "The signing link will be texted to this number."}
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                data-testid="roi-create-submit-btn"
                className="gap-2"
              >
                <Send className="w-4 h-4" />
                {submitting ? "Creating..." : sendEmail ? "Create & Send" : "Create Link"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
